import { baseApi } from 'shared/api/base-api';
import type { ResolverContext, ResolverTeam } from 'modules/manager/domain/placeholder';

interface RenderTeamDto {
  id: number;
  name: string;
  shortName: string;
  emblem?: { externalUrl?: string };
}

interface RenderTeamMatchDto {
  teamOrder?: number;
  score?: number;
  team?: RenderTeamDto;
}

interface RenderMatchDto {
  id: number;
  date?: string;
  location?: { name?: string };
  tournament?: { id: number; name: string };
  championship?: { id: number; name: string; images?: { externalUrl?: string }[] };
  teamMatches?: RenderTeamMatchDto[];
}

interface RenderDataResponse {
  match: RenderMatchDto | null;
}

export interface RenderDataArgs {
  matchId: number;
}

const RENDER_DATA_QUERY = `#graphql
query getRenderData($matchId: Int!) {
  match(id: $matchId) {
    id
    date
    location { name }
    tournament { id name }
    championship { id name images { externalUrl } }
    teamMatches {
      teamOrder
      score
      team {
        id
        name
        shortName
        emblem { externalUrl }
      }
    }
  }
}
`;

export const renderApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getRenderContext: build.query<ResolverContext, RenderDataArgs>({
      query: ({ matchId }) => ({ query: RENDER_DATA_QUERY, variables: { matchId } }),
      transformResponse: (raw: RenderDataResponse): ResolverContext => {
        const match = raw?.match;
        if (!match) throw new Error('Match not found');
        const home = pickTeam(match.teamMatches, 1);
        const away = pickTeam(match.teamMatches, 2);
        if (!home || !away) throw new Error('Match is missing one of the teams');
        return {
          tournament: {
            id: match.tournament?.id ?? 0,
            name: match.tournament?.name ?? '',
          },
          championship: {
            id: match.championship?.id ?? 0,
            name: match.championship?.name ?? '',
            logoUrl: match.championship?.images?.[0]?.externalUrl,
          },
          match: {
            id: match.id,
            date: match.date,
            venue: match.location?.name,
            homeScore: home.score,
            awayScore: away.score,
          },
          homeTeam: toResolverTeam(home),
          awayTeam: toResolverTeam(away),
        };
      },
      keepUnusedDataFor: 30,
    }),
  }),
  overrideExisting: false,
});

function pickTeam(teamMatches: RenderTeamMatchDto[] | undefined, order: number): RenderTeamMatchDto | undefined {
  return teamMatches?.find((tm) => tm.teamOrder === order);
}

function toResolverTeam(tm: RenderTeamMatchDto): ResolverTeam {
  const team = tm.team;
  return {
    id: team?.id ?? 0,
    name: team?.name ?? '',
    shortName: team?.shortName ?? '',
    logoUrl: team?.emblem?.externalUrl,
  };
}

export const { useGetRenderContextQuery } = renderApi;
